import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { GAMES } from "@/lib/games";
import { formatDate, cycleDateIST } from "@/lib/format";
import { FloppyDisk, Trash, PencilSimple, EyeSlash, Eye } from "@phosphor-icons/react";

export default function ManageResults() {
  const [gameSlug, setGameSlug] = useState(GAMES[0].slug);
  const game = GAMES.find((g) => g.slug === gameSlug);
  const [date, setDate] = useState(cycleDateIST(GAMES[0]));
  const [fr, setFr] = useState("");
  const [sr, setSr] = useState("");
  const [published, setPublished] = useState(true);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadReqId = useRef(0);

  const load = async () => {
    const reqId = ++loadReqId.current;
    setLoadingRows(true);
    try {
      const r = await api.get(`/admin/results?game_slug=${gameSlug}&limit=120`);
      if (reqId === loadReqId.current) setRows(r.data);
    } finally {
      if (reqId === loadReqId.current) setLoadingRows(false);
    }
  };

  useEffect(() => {
    setRows([]);          // clear stale rows immediately when game changes
    load();
    // when switching game, prefill date to the game's cycle date
    setDate(cycleDateIST(GAMES.find((g) => g.slug === gameSlug)));
    setFr(""); setSr(""); setPublished(true);
    // eslint-disable-next-line
  }, [gameSlug]);

  async function save(e) {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/results", {
        game_slug: gameSlug,
        result_date: date,
        fr: fr ? fr.padStart(2, "0") : null,
        sr: sr ? sr.padStart(2, "0") : null,
        published,
      });
      toast.success(published ? "Result published" : "Result saved as draft");
      setFr(""); setSr("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function edit(r) {
    setDate(r.result_date);
    setFr(r.fr || "");
    setSr(r.sr || "");
    setPublished(r.published);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function togglePublish(r) {
    try {
      await api.post("/admin/results", {
        game_slug: r.game_slug,
        result_date: r.result_date,
        fr: r.fr,
        sr: r.sr,
        published: !r.published,
      });
      toast.success(r.published ? "Unpublished" : "Published");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function remove(r) {
    const gname = GAMES.find((g) => g.slug === r.game_slug)?.name || r.game_slug;
    if (!confirm(`Delete result for ${gname} on ${formatDate(r.result_date)}?`)) return;
    try {
      await api.delete(`/admin/results/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div>
      <h1 className="font-heading font-black text-3xl">Manage Results</h1>
      <p className="text-sm text-neutral-600 mt-1">
        Publish today's FR/SR for a game — the homepage, previous results and common numbers update automatically.
      </p>

      <form onSubmit={save} className="mt-5 bg-white border border-neutral-200 rounded-2xl p-5 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="results-form">
        <div className="md:col-span-2">
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Game</label>
          <select value={gameSlug} onChange={(e) => setGameSlug(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 bg-white text-sm" data-testid="results-game">
            {GAMES.map((g) => <option key={g.slug} value={g.slug}>{g.number.toString().padStart(2, "0")}. {g.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Date (IST)</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 text-sm" data-testid="results-date" required />
          <button type="button" onClick={() => setDate(cycleDateIST(game))} className="text-[10px] text-blue-700 hover:underline mt-1">
            Use current cycle ({formatDate(cycleDateIST(game))})
          </button>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">F/R</label>
          <input inputMode="numeric" pattern="\d{0,2}" maxLength={2} value={fr} onChange={(e) => setFr(e.target.value.replace(/\D/g, ""))} className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 text-sm font-numbers font-bold" data-testid="results-fr" placeholder="00" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">S/R</label>
          <input inputMode="numeric" pattern="\d{0,2}" maxLength={2} value={sr} onChange={(e) => setSr(e.target.value.replace(/\D/g, ""))} className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 text-sm font-numbers font-bold" data-testid="results-sr" placeholder="00" />
        </div>
        <div className="flex items-end gap-2">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="results-published" />
            <span>Publish</span>
          </label>
        </div>
        <div className="md:col-span-6 flex gap-2">
          <button disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-500 disabled:opacity-60" data-testid="results-save">
            <FloppyDisk size={16} weight="bold" /> {busy ? "Saving…" : (published ? "Publish" : "Save Draft")}
          </button>
        </div>
      </form>

      <div className="mt-6 bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-2.5 font-bold">Date</th>
              <th className="px-4 py-2.5 font-bold">F/R</th>
              <th className="px-4 py-2.5 font-bold">S/R</th>
              <th className="px-4 py-2.5 font-bold">Status</th>
              <th className="px-4 py-2.5 font-bold">Source</th>
              <th className="px-4 py-2.5 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="results-table">
            {loadingRows && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-neutral-500">Loading…</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100">
                <td className="px-4 py-2.5">{formatDate(r.result_date)}</td>
                <td className="px-4 py-2.5 font-numbers font-bold">{r.fr ?? "--"}</td>
                <td className="px-4 py-2.5 font-numbers font-bold">{r.sr ?? "--"}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-bold ${r.published ? "text-emerald-600" : "text-neutral-500"}`}>
                    {r.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-2.5"><span className={`text-[10px] font-bold uppercase tracking-widest ${r.is_demo ? "text-amber-600" : "text-neutral-400"}`}>{r.is_demo ? "Demo" : "Official"}</span></td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => togglePublish(r)} className="p-1.5 rounded hover:bg-neutral-100" aria-label={r.published ? "Unpublish" : "Publish"} data-testid={`row-toggle-${r.id}`}>
                      {r.published ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
                    </button>
                    <button onClick={() => edit(r)} className="p-1.5 rounded hover:bg-neutral-100" aria-label="Edit" data-testid={`row-edit-${r.id}`}><PencilSimple size={14} weight="bold" /></button>
                    <button onClick={() => remove(r)} className="p-1.5 rounded hover:bg-red-50 text-red-600" aria-label="Delete" data-testid={`row-delete-${r.id}`}><Trash size={14} weight="bold" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loadingRows && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-neutral-500">No results.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
